import { useEffect, useRef, useState } from "react";
import { Eyebrow } from "../atoms/Eyebrow";
import { Reveal } from "../atoms/Reveal";
import { IconPhone } from "../atoms/icons";
import type { Copy, Transcript } from "../lib/copy";

type FonioProps = { t: Copy };

export function Fonio({ t }: FonioProps) {
  return (
    <section id="kova" className="section fonio">
      <div className="container">
        <div className="fonio-grid">
          <div className="fonio-left">
            <Reveal>
              <Eyebrow className="on-dark">{t.fonio.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal as="h2" className="section-title on-dark" delay={60}>
              <span className="ink-inv">{t.fonio.title_a}</span>{" "}
              <span className="serif-accent accent-blue">{t.fonio.title_b}</span>
            </Reveal>
            <Reveal as="p" className="section-sub on-dark" delay={120}>
              {t.fonio.sub}
            </Reveal>

            <Reveal as="ul" className="fonio-bullets" delay={180}>
              {t.fonio.bullets.map((b, i) => (
                <li key={i}>
                  <span className="tick tick-inv" />
                  {b}
                </li>
              ))}
            </Reveal>

            <Reveal className="fonio-stats" delay={240}>
              {t.fonio.stats.map((s, i) => (
                <div key={i} className="fs">
                  <div className="fs-v">{s.v}</div>
                  <div className="fs-l">{s.l}</div>
                </div>
              ))}
            </Reveal>

            <Reveal delay={300}>
              <a href="tel:+43720080785" className="kova-call-cta">
                <span className="kova-call-cta-icon" aria-hidden="true">
                  <IconPhone />
                </span>
                <span className="kova-call-cta-text">
                  <span className="kova-call-cta-label">Selber testen — direkt anrufen</span>
                  <span className="kova-call-cta-num">+43 720 080785</span>
                </span>
              </a>
            </Reveal>
          </div>

          <Reveal className="fonio-right" delay={200}>
            <FonioCallVisual transcript={t.fonio.transcript} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/**
 * Animated "live call" card — bubbles stream in one-at-a-time on a loop as
 * soon as the component scrolls into view, with a typing indicator between
 * messages to sell the real-time feeling.
 */
function FonioCallVisual({ transcript }: { transcript: Transcript[] }) {
  const [step, setStep] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;
    let timer: number | undefined;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !started) {
            started = true;
            let i = 1;
            setStep(1);
            timer = window.setInterval(() => {
              i = (i % transcript.length) + 1;
              setStep(i);
            }, 2600);
            io.unobserve(el);
          }
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [transcript.length]);

  return (
    <div ref={ref} className="fcv">
      <div className="fcv-top">
        <div className="fcv-caller">
          <div className="fcv-avatar" aria-hidden="true" />
          <div>
            <div className="fcv-name">+43 720 080785</div>
            <div className="fcv-tag">Live · DE</div>
          </div>
        </div>
        <Waveform />
        <div className="fcv-timer">00:42</div>
      </div>

      <div className="fcv-transcript">
        {transcript.slice(0, step).map((m, i) => (
          <div key={i} className={"fcv-bubble " + (m.who === "ai" ? "ai" : "caller")}>
            <span className="fcv-who">{m.who === "ai" ? "KOVA AI" : "Caller"}</span>
            <span className="fcv-text">{m.text}</span>
          </div>
        ))}
        {step < transcript.length && (
          <div className="fcv-typing">
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      <div className="fcv-actions">
        <span className="fcv-chip">PMS: Mews · Verfügbarkeit geprüft</span>
        <span className="fcv-chip ok">Buchung angelegt</span>
      </div>
    </div>
  );
}

function Waveform() {
  return (
    <div className="waveform fcv-wave" aria-hidden="true">
      {Array.from({ length: 36 }).map((_, i) => (
        <span key={i} style={{ animationDelay: i * 55 + "ms" }} />
      ))}
    </div>
  );
}
